import { fromZonedTime } from "date-fns-tz";

/**
 * Lógica pura de vigência/validação para itens de FAQ e campanhas datadas.
 *
 * Um `faqItem` tem o shape (espelha o backend):
 *   { question, answer, type?: "faq"|"campaign", validFrom?, validUntil? }
 * Datas são ISO date puro ("YYYY-MM-DD"). Sem `type:"campaign"` (ou sem datas)
 * o item é uma FAQ perene ("Sempre ativa").
 *
 * A semântica de borda é BRT (America/Sao_Paulo) fim-de-dia — IGUAL ao backend:
 *   • início da campanha  = validFrom  às 00:00:00.000 BRT
 *   • fim da campanha      = validUntil às 23:59:59.999 BRT
 * Assim um `validUntil` = "hoje" continua vigente até o fim do dia no Brasil.
 */

const TZ = "America/Sao_Paulo";

// ── Helpers de data ──────────────────────────────────────────────────────────

// Valida estritamente o formato ISO date "YYYY-MM-DD" (e que é uma data real).
function isValidIsoDate(value) {
  if (typeof value !== "string") return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  // Confirma que o dia existe no mês (ex.: rejeita 2026-02-30).
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
}

// Instante UTC do início do dia (00:00:00.000) da data-calendário em BRT.
function startOfDayBrt(isoDate) {
  return fromZonedTime(`${isoDate}T00:00:00.000`, TZ);
}

// Instante UTC do fim do dia (23:59:59.999) da data-calendário em BRT.
function endOfDayBrt(isoDate) {
  return fromZonedTime(`${isoDate}T23:59:59.999`, TZ);
}

// Formata "YYYY-MM-DD" como "DD/MM" (sem tocar em fuso — é data-calendário).
function formatDdMm(isoDate) {
  if (!isValidIsoDate(isoDate)) return "";
  const [, m, d] = isoDate.split("-");
  return `${d}/${m}`;
}

// Data-calendário "YYYY-MM-DD" de hoje no fuso BRT (para a regra isNew).
function todayIsoBrt(now = new Date()) {
  // en-CA formata como YYYY-MM-DD; timeZone garante a data-calendário BRT.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

// ── Status de vigência ───────────────────────────────────────────────────────

/**
 * Calcula o estado de vigência de um item para exibição (selo).
 *
 * @param {object} item  { type?, validFrom?, validUntil? }
 * @param {Date}   [now] instante de referência (default: agora). Passe um valor
 *                       fixo em testes para não depender do relógio real.
 * @returns {{ state: "perennial"|"active"|"expired"|"scheduled", label: string,
 *             tone: "neutral"|"success"|"warning"|"danger" }}
 */
export function computeFaqStatus(item, now = new Date()) {
  const isCampaign = item?.type === "campaign";
  const validFrom = item?.validFrom;
  const validUntil = item?.validUntil;

  // FAQ perene: sem type de campanha OU sem nenhuma data.
  if (!isCampaign || (!validFrom && !validUntil)) {
    return { state: "perennial", label: "Sempre ativa", tone: "neutral" };
  }

  // Agendada: ainda não começou (now antes do início BRT).
  if (isValidIsoDate(validFrom) && now < startOfDayBrt(validFrom)) {
    return {
      state: "scheduled",
      label: `Agendada (começa em ${formatDdMm(validFrom)})`,
      tone: "warning",
    };
  }

  // Expirada: passou do fim do dia BRT do validUntil.
  if (isValidIsoDate(validUntil) && now > endOfDayBrt(validUntil)) {
    return {
      state: "expired",
      label: "Expirada",
      tone: "danger",
    };
  }

  // Vigente.
  return {
    state: "active",
    label: isValidIsoDate(validUntil)
      ? `Vigente (expira em ${formatDdMm(validUntil)})`
      : "Vigente",
    tone: "success",
  };
}

// ── Validação (espelha o Zod do backend) ─────────────────────────────────────

/**
 * Valida um item de FAQ/campanha, espelhando o schema Zod do backend:
 *   • question: 1–200 caracteres
 *   • answer:   1–500 caracteres
 *   • se type === "campaign": exige validUntil válido
 *   • datas: formato "YYYY-MM-DD" válido
 *   • se ambas presentes: validFrom <= validUntil
 *
 * ⚠️ A regra `validUntil >= hoje` só se aplica a itens NOVOS (`isNew`). Um item
 * já existente (mesmo expirado) NÃO é bloqueado por data no passado — o dono
 * pode estar apenas estendendo/editando; nesse caso mostramos só o selo.
 *
 * @param {object} item
 * @param {{ isNew?: boolean, now?: Date }} [opts]
 * @returns {{ valid: boolean, errors: { question?: string, answer?: string,
 *             validFrom?: string, validUntil?: string } }}
 */
export function validateFaqItem(item, { isNew = false, now = new Date() } = {}) {
  const errors = {};
  const question = typeof item?.question === "string" ? item.question : "";
  const answer = typeof item?.answer === "string" ? item.answer : "";
  const isCampaign = item?.type === "campaign";
  const validFrom = item?.validFrom;
  const validUntil = item?.validUntil;

  // question 1–200
  if (question.trim().length === 0) {
    errors.question = "A pergunta é obrigatória.";
  } else if (question.length > 200) {
    errors.question = "A pergunta deve ter no máximo 200 caracteres.";
  }

  // answer 1–500
  if (answer.trim().length === 0) {
    errors.answer = "A resposta é obrigatória.";
  } else if (answer.length > 500) {
    errors.answer = "A resposta deve ter no máximo 500 caracteres.";
  }

  if (isCampaign) {
    // validFrom é opcional, mas se informado precisa ser válido.
    if (validFrom != null && validFrom !== "" && !isValidIsoDate(validFrom)) {
      errors.validFrom = "Data de início inválida.";
    }

    // validUntil é obrigatório na campanha.
    if (validUntil == null || validUntil === "") {
      errors.validUntil = "Defina a data de validade da campanha.";
    } else if (!isValidIsoDate(validUntil)) {
      errors.validUntil = "Data de validade inválida.";
    } else if (isNew && validUntil < todayIsoBrt(now)) {
      // Comparação lexicográfica de "YYYY-MM-DD" funciona como cronológica.
      errors.validUntil = "A validade não pode ser no passado.";
    }

    // Coerência: início <= fim (só quando ambas são válidas).
    if (
      !errors.validFrom &&
      !errors.validUntil &&
      isValidIsoDate(validFrom) &&
      isValidIsoDate(validUntil) &&
      validFrom > validUntil
    ) {
      errors.validFrom = "O início deve ser anterior ou igual à validade.";
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}
